import { Select, type SelectProps } from "antd/es";
import type { RefSelectProps } from "antd/es/select";
import React from "react";

import { useFormFieldContext } from "~/components/form/use-form-field-context";
import { type VariantProp, type VariantProps, cv } from "~/tools/class-variance";
import { useScoped } from "~/tools/impulse";
import { useOnInvalidFormFocus } from "~/tools/impulse-form/use-on-invalid-form-focus";
import { mergeRefs } from "~/tools/merge-refs";
import type { Record } from "~/tools/record";

import styles from "./_multi-select.module.css";

const variants = cv(styles.multiSelect, {
	variants: {
		variant: {
			neutral: styles.neutral,
			error: styles.error,
		},
		size: {
			sm: styles.sm,
			md: styles.md,
			lg: styles.lg,
		},
	},
	defaultVariants: {
		size: "md",
		variant: "neutral",
	},
});

const SIZE_TO_ANTD_SIZE = {
	sm: "small",
	md: "middle",
	lg: "large",
} as const satisfies Record<VariantProp<typeof variants, "size">>;

export interface MultiSelectProps
	extends VariantProps<typeof variants>,
		Omit<
			SelectProps<ReadonlyArray<string>>,
			"value" | "defaultValue" | "searchValue" | "mode" | "size" | "onChange" | "onSearch" | "onSelect" | "onDeselect"
		> {
	loading?: boolean;
	values?: ReadonlyArray<string>;
	onValuesChange?: (values: Array<string>) => void;

	search?: string;
	onSearchChange?: (search: string) => void;

	onSelect?: (value: string) => void;
	onDeselect?: (value: string) => void;
}

export const MultiSelect = React.forwardRef<RefSelectProps, MultiSelectProps>(
	(
		{ className, loading, disabled, variant, size, search, values, onValuesChange, onBlur, onSearchChange, ...props },
		ref,
	) => {
		const formField = useFormFieldContext();
		const focusRef = useOnInvalidFormFocus<RefSelectProps>(formField?.state);
		const submitting = useScoped((scope) => formField?.state.isSubmitting(scope), [formField?.state]);
		const invalid = useScoped((scope) => formField?.state.isInvalid(scope), [formField?.state]);
		const busy = loading || submitting;

		return (
			<Select<Array<string>>
				id={formField?.id}
				ref={mergeRefs([ref, focusRef])}
				{...props}
				aria-busy={busy}
				className={variants({ className, variant: invalid ? "error" : variant, size })}
				disabled={busy || disabled}
				mode="multiple"
				onBlur={(event) => {
					formField?.state.setTouched(true);
					onBlur?.(event);
				}}
				onChange={onValuesChange}
				onSearch={onSearchChange}
				searchValue={search}
				size={size == null ? undefined : SIZE_TO_ANTD_SIZE[size]}
				value={values as undefined | Array<string>}
			/>
		);
	},
);
